import { CompositeFilterDescriptor, DataSourceRequestState, FilterDescriptor, State as GridState, toDataSourceRequestString } from '@progress/kendo-data-query';
import { formatNumber } from '@progress/kendo-intl';
import { Button } from '@progress/kendo-react-buttons';
import { GridColumn as Column, Grid, GridCustomCellProps, GridDataStateChangeEvent, GridEvent, GridFilterChangeEvent, GridHandle, GridSortChangeEvent, GridToolbar } from '@progress/kendo-react-grid';
import { Loader } from "@progress/kendo-react-indicators";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';
import CenterDivPanel from '../../components/CenterDivPanel';
import { DropdownFilterCellIDName } from '../../components/cells/DropdownFilterCellIDName';
import EmailCell from '../../components/cells/EmailCell';
import PhoneCellAndExtension from '../../components/cells/PhoneCellAndExtension';
import { fetchApi } from '../../services/api';
import { ILink } from '../../types/link';
import { Title } from '../../utils/title';
import { contactTypeOptions } from './ContactTypeOptions';

export type CustomerContactViewModel = {
  CustomerContactID: number;
  FullName: string;
  FirstName: string;
  LastName: string;
  PhoneNumber: string;
  PhoneNumberExtension: string;
  EMailAddress: string;
  Title: string;
  ContactType: string;
  ContactTypeName: string;
  Active: boolean;
  CustomerNumber: string;
  CustomerName: string;
  Hash: string;
  Links: ILink[];
}

const PageSize = 50;

export const CustomerContactCell = (props: GridCustomCellProps) => {
  if (!props.field)
    return null;

  const dataItem: CustomerContactViewModel = props.dataItem;
  const customerContactProfile = dataItem.Links.find((x: ILink) => x.Name === 'CustomerContactProfile');
  return (
    <td {...props.tdProps}>
      {customerContactProfile &&
        <Link
          to={customerContactProfile.Link}
          style={{ color: '#007bff' }}
        >
          {dataItem.FullName || "N/A"}
        </Link>}
    </td>
  );
};

export const ContactTypeNameCell = (props: GridCustomCellProps) => {
  if (!props.field)
    return null;

  const dataItem: CustomerContactViewModel = props.dataItem;
  return (
    <td {...props.tdProps}>
      {dataItem.ContactTypeName}
    </td>
  );
};

const CustomerContactGrid = () => {
  const resetDataState = {
    skip: 0,
    take: PageSize,
    sort: [{
      field: "FullName",
      dir: "asc"
    }]
  } as GridState;

  const defaultAndFilter = {
    logic: 'and', filters: [
      { field: 'Active', operator: 'eq', value: true },
    ]
  } as CompositeFilterDescriptor;

  const sessionStorageAndFilter = sessionStorage.getItem("CustomerContactGrid-andFilter");
  let andFilter: CompositeFilterDescriptor = null;
  if (sessionStorageAndFilter) {
    andFilter = JSON.parse(sessionStorageAndFilter);
  } else {
    andFilter = { ...defaultAndFilter };
  }
  const [dataState, setDataState] = useState<DataSourceRequestState>({ ...resetDataState, filter: andFilter });
  const [loading, setLoading] = useState(false);
  const [customerContacts, setCustomerContacts] = useState<CustomerContactViewModel[]>([]);
  const gridRef = useRef<GridHandle>(null);
  const [totalRecords, setTotalRecords] = useState(0);

  const handleResize = (_e: UIEvent) => {
    if ((document.getElementsByClassName("k-grid")[0]) as HTMLDivElement != null) {
      ((document.getElementsByClassName("k-grid")[0]) as HTMLDivElement).style.height = (document.documentElement.clientHeight - 250) + "px";
    }
  }

  useLayoutEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    }
  }, [])

  const fetchRecords = useCallback((append: boolean) => {
    sessionStorage.setItem("CustomerContactGrid-andFilter", JSON.stringify(dataState.filter));
    setLoading(true);
    const dataGridState = { ...dataState };
    if (!append) {
      dataGridState.take = PageSize;
      dataGridState.skip = 0;
      window.scrollTo(0, 0);
      gridRef.current?.element?.querySelector<HTMLElement>('.k-grid-content')?.scrollTo(0, 0);
    }

    //Todo - Add as a top filter
    const data = {
      CompanyActive: true
    };
    const queryStr = `${toDataSourceRequestString(dataGridState)}`;
    fetchApi(`/api/Customer/CustomerContacts?${queryStr}`, data, 'POST')
      .then(({ Data, Total }) => {
        setTotalRecords(Total);
        if (!append) {
          setCustomerContacts(Data);
        } else {
          setCustomerContacts(prevData => prevData.concat(Data));
        }
        setLoading(false);
        handleResize(null);
      }).catch(() => {
        setLoading(false);
      });
  }, [dataState]);

  const search = useDebouncedCallback(useCallback(() => {
    fetchRecords(false);
  }, [fetchRecords]), 750);

  useEffect(() => {
    search();
  }, [dataState.filter, dataState.sort, search]);

  const scrollHandler = (event: GridEvent) => {
    const scrollElement = event.syntheticEvent.currentTarget as HTMLElement;
    const isNearBottom = scrollElement.scrollHeight > scrollElement.clientHeight
      && scrollElement.scrollTop + scrollElement.clientHeight >= scrollElement.scrollHeight - 10;

    if (!isNearBottom || loading || customerContacts.length >= totalRecords)
      return;

    dataState.skip = customerContacts.length;
    fetchRecords(true);
  }

  const CustomerCell = (props: GridCustomCellProps) => {
    if (!props.field)
      return null;

    const dataItem: CustomerContactViewModel = props.dataItem;
    const customerProfile = dataItem.Links.find((x: ILink) => x.Name === 'CustomerProfile');
    //props.dataItem[props.field]
    return (
      <td {...props.tdProps}>
        {customerProfile &&
          <Link
            to={customerProfile.Link}
            style={{ color: '#007bff' }}
          >
            {dataItem.CustomerNumber}
          </Link>} - {dataItem.CustomerName}
      </td>
    );
  };

  const dataStateChange = (changeEvent: GridDataStateChangeEvent) => {
    setDataState(changeEvent.dataState);
  }

  const onGridFilterChange = (event: GridFilterChangeEvent) => {
    setDataState({ ...dataState, filter: event.filter ?? { ...defaultAndFilter } });
  };

  const onGridSortChange = (event: GridSortChangeEvent) => {
    setDataState({ ...dataState, sort: event.sort });
  };

  const activeFilterValue = () => (((dataState.filter as CompositeFilterDescriptor)?.filters as FilterDescriptor[] | undefined)?.find(x => x.field === 'Active')?.value as boolean) ?? true;

  const toggleActiveFilter = () => {
    const compositeFilter = (dataState.filter as CompositeFilterDescriptor) ?? { logic: 'and', filters: [] };
    const filters = ([...(compositeFilter.filters as FilterDescriptor[] ?? [])]);
    const activeFilterIndex = filters.findIndex(filter => filter.field === 'Active');

    if (activeFilterIndex === -1) {
      filters.push({ field: 'Active', operator: 'eq', value: true });
    } else {
      const currentFilter = filters[activeFilterIndex];
      filters[activeFilterIndex] = { ...currentFilter, value: !(currentFilter.value as boolean) };
    }

    const newFilter: CompositeFilterDescriptor = {
      logic: compositeFilter.logic ?? 'and',
      filters
    };

    setDataState({ ...dataState, filter: newFilter });
  };

  return <>
      <br />
      <Title string="Customer Contact Listing" />
      {loading && <CenterDivPanel>
        <Loader type="converging-spinner" />
      </CenterDivPanel>}
      <Grid
        ref={gridRef}
        {...dataState}
        scrollable="scrollable"
        onDataStateChange={dataStateChange}
        data={customerContacts}
        resizable={true}
        reorderable={true}
        sortable={true}
        onScroll={scrollHandler}
        filterable={true}
        onFilterChange={(e) => onGridFilterChange(e)}
        onSortChange={(e) => onGridSortChange(e)}
      >
        <GridToolbar>
          <Button
            themeColor={activeFilterValue() ? 'primary' : 'warning'}
            onClick={toggleActiveFilter}
          >
            {activeFilterValue() ? 'Active' : 'Retired'}
          </Button>
        </GridToolbar>
        <Column field="FullName" title="Name" cells={{ data: CustomerContactCell }} />
        <Column field="EMailAddress" title="E-Mail" cells={{ data: EmailCell }} />
        <Column field="PhoneNumber" title="Phone" cells={{ data: PhoneCellAndExtension }} />
        <Column
          field="ContactType"
          title="Contact Type"
          cells={{
            data: ContactTypeNameCell,
            filterCell: (props) => (
              <DropdownFilterCellIDName
                {...props}
                data={contactTypeOptions}
              />
            )
          }}
        />
        <Column field="Title" title="Title" />
        <Column field="CustomerNumber" title="Customer" cells={{ data: CustomerCell }} />
      </Grid>
      {
        totalRecords > 0 &&
        <div className="k-pager k-pager-md k-grid-pagert">
          <div style={{ marginLeft: "auto", marginRight: 0 }}>
            {formatNumber(Math.min(customerContacts.length, totalRecords), 'n0')} of {formatNumber(totalRecords, 'n0')} items
          </div>
        </div>
      }
  </>
}

export default CustomerContactGrid;
